import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";

/* Login screen — Clerk-backed sign-in / sign-up.
   On success: upsert the user into Supabase, then route to the dashboard. */

   function LoginScreen({ onLogin, onBack }) {
    const completedSessionRef = React.useRef(null);
    const [status, setStatus] = React.useState("loading"); // loading | ready | error
    const [errorMsg, setErrorMsg] = React.useState("");
    const [pending, setPending] = React.useState(null); // "google" | "apple" | null
  
    const handleSignedIn = React.useCallback(async (user, options = {}) => {
      if (!user) return;
      const { isNewUser = false } = options;
      const sessionId = window.Clerk?.session?.id || user.id;
      if (completedSessionRef.current === sessionId) return;
      completedSessionRef.current = sessionId;
  
      const email =
        user.primaryEmailAddress?.emailAddress ||
        user.emailAddresses?.[0]?.emailAddress ||
        "you@example.com";
  
      try {
        const nextUrl = new URL(window.location.href);
        let changed = false;
        Array.from(nextUrl.searchParams.keys()).forEach((key) => {
          if (key.startsWith("__clerk_")) {
            nextUrl.searchParams.delete(key);
            changed = true;
          }
        });
        if (nextUrl.pathname === "/") {
          nextUrl.pathname = "/dashboard";
          changed = true;
        }
        if (changed) {
          window.history.replaceState(window.history.state, "", nextUrl.toString());
        }
      } catch (e) {
        console.warn("Could not clean auth callback URL:", e);
      }
  
      try {
        let supabaseUser = null;
        if (typeof window.upsertSupabaseUser === "function") {
          supabaseUser = await window.upsertSupabaseUser(user);
        }
        onLogin({ email, user: supabaseUser, isNewUser });
      } catch (e) {
        console.warn("Supabase upsert error:", e);
        onLogin({ email, isNewUser });
      }
    }, [onLogin]);
  
    React.useEffect(() => {
      let cancelled = false;
      let removeListener = null;
  
      const waitForClerk = async () => {
        const start = Date.now();
        while (!window.Clerk && Date.now() - start < 10000) {
          await new Promise((r) => setTimeout(r, 50));
        }
        return !!window.Clerk;
      };
  
      // After an OAuth redirect, the Clerk JS SDK may have completed a sign-in,
      // OR landed in a "transferable" state because the user doesn't yet have
      // an account (sign-in case) or already has one (sign-up case). The
      // mounted <SignIn /> widget used to handle these transfers itself; with
      // a custom button we have to do it explicitly. See Clerk's
      // "OAuth with Account Transfer" docs.
      const finishOAuthCallback = async () => {
        const client = window.Clerk.client;
        const signIn = client?.signIn;
        const signUp = client?.signUp;

        if (window.Clerk.user && window.Clerk.session) {
          handleSignedIn(window.Clerk.user);
          return true;
        }

        // Existing user came in through sign-up flow → transfer to sign-in.
        if (signUp?.verifications?.externalAccount?.status === "transferable") {
          try {
            const res = await signIn.create({ transfer: true });
            if (res.status === "complete") {
              await window.Clerk.setActive({ session: res.createdSessionId });
              if (window.Clerk.user) handleSignedIn(window.Clerk.user, { isNewUser: false });
              return true;
            }
          } catch (e) {
            console.error("Sign-in transfer failed:", e);
          }
        }

        // New user came in through sign-in flow → transfer to sign-up.
        if (signIn?.firstFactorVerification?.status === "transferable") {
          try {
            const res = await signUp.create({ transfer: true });
            if (res.status === "complete") {
              await window.Clerk.setActive({ session: res.createdSessionId });
              if (window.Clerk.user) handleSignedIn(window.Clerk.user, { isNewUser: true });
              return true;
            }
            if (res.status === "missing_requirements") {
              // Some social providers (Apple in particular) may not return an
              // email on subsequent logins. Try once to complete with whatever
              // OAuth supplied; otherwise surface a readable error.
              try {
                const done = await signUp.update({});
                if (done.status === "complete") {
                  await window.Clerk.setActive({ session: done.createdSessionId });
                  if (window.Clerk.user) handleSignedIn(window.Clerk.user, { isNewUser: true });
                  return true;
                }
              } catch (e2) {
                console.error("Sign-up completion failed:", e2);
              }
              setErrorMsg("We couldn't finish creating your account. Please try a different provider.");
              setStatus("error");
              return true;
            }
          } catch (e) {
            console.error("Sign-up transfer failed:", e);
            setErrorMsg(e?.errors?.[0]?.longMessage || "Could not create your account. Please try again.");
            setStatus("error");
            return true;
          }
        }

        return false;
      };

      const init = async () => {
        const present = await waitForClerk();
        if (cancelled) return;
        if (!present) {
          setErrorMsg("Could not load sign-in. Check your network and reload.");
          setStatus("error");
          return;
        }

        try {
          if (!window.Clerk.loaded) {
            await window.Clerk.load();
          }
        } catch (e) {
          console.error(e);
          if (!cancelled) {
            setErrorMsg("Failed to initialize sign-in.");
            setStatus("error");
          }
          return;
        }
        if (cancelled) return;

        const hasClerkCallback = Array.from(new URLSearchParams(window.location.search).keys())
          .some((key) => key.startsWith("__clerk_"));

        if (hasClerkCallback) {
          const handled = await finishOAuthCallback();
          if (cancelled) return;
          if (handled) return;
        }

        // If our app considers the user signed out (LoginScreen only mounts
        // when cn_auth_v1 is gone), but Clerk still holds a session from a
        // previous run, tear it down. Otherwise the next OAuth click would
        // silently re-auth the previous user instead of showing the chooser.
        if (window.Clerk.user || window.Clerk.session) {
          try { await window.Clerk.signOut(); } catch (e) {
            console.warn("Clerk signOut failed:", e);
          }
          if (cancelled) return;
        }

        // Only forward on a fresh sign-in event triggered by user action.
        let armed = false;
        removeListener = window.Clerk.addListener((payload) => {
          if (!armed) return;
          if (payload?.user && payload?.session) {
            handleSignedIn(payload.user);
          }
        });
        setTimeout(() => { armed = true; }, 0);

        setStatus("ready");
      };
  
      init();
  
      return () => {
        cancelled = true;
        try { removeListener && removeListener(); } catch (e) {}
      };
    }, [handleSignedIn]);
  
    const startOAuth = React.useCallback(async (strategy, key) => {
      if (!window.Clerk?.client?.signIn) {
        setErrorMsg("Sign-in is not ready yet. Please try again.");
        setStatus("error");
        return;
      }
      setPending(key);
      try {
        const here = window.location.origin + window.location.pathname;
        await window.Clerk.client.signIn.authenticateWithRedirect({
          strategy,
          redirectUrl: here,
          redirectUrlComplete: here,
        });
      } catch (e) {
        console.error("OAuth start failed:", e);
        setPending(null);
        setErrorMsg(e?.errors?.[0]?.longMessage || e?.message || "Could not start sign-in.");
      }
    }, []);
  
    const busy = pending !== null;
    const disabled = status !== "ready" || busy;
  
    const GoogleMark = (
      <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.08-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.46-.8 5.95-2.18l-2.9-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.33A8.997 8.997 0 0 0 9 18z"/>
        <path fill="#FBBC05" d="M3.95 10.71A5.41 5.41 0 0 1 3.66 9c0-.59.1-1.17.29-1.71V4.96H.96A8.997 8.997 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.99-2.33z"/>
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A8.997 8.997 0 0 0 .96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58z"/>
      </svg>
    );
  
    const AppleMark = (
      <svg width="18" height="18" viewBox="0 0 384 512" aria-hidden="true">
        <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM256.8 84.9c30.7-36.4 27.9-69.6 27-81.5-27.1 1.6-58.4 18.5-76.3 39.3-19.7 22.3-31.3 49.9-28.8 80.5 29.3 2.3 56.1-12.8 78.1-38.3z"/>
      </svg>
    );
  
    return (
      <div className="auth-wrap screen-enter">
        <style>{`@keyframes authButtonSpin { to { transform: rotate(360deg); } }`}</style>
        <Seo
          title="Sign in"
          description="Sign in to Community Ninja to discover Reddit conversations that match your product."
          noindex
        />
        {onBack && (
          <button
            className="auth-theme"
            onClick={onBack}
            aria-label="Back"
            title="Back"
            style={{ left: 16, right: "auto" }}
          >
            <window.I.ArrowLeft size={15} />
          </button>
        )}
        <div
          className="auth-card"
          style={{ maxWidth: 380, padding: "32px 28px", gap: 18, textAlign: "center", alignItems: "stretch" }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <img
              src="/assets/icon.png"
              alt="Community Ninja"
              className="logo-mark"
              style={{ width: 44, height: 44, borderRadius: 12, background: "none" }}
            />
          </div>
  
          <div>
            <h1 className="auth-title" style={{ margin: 0, fontSize: 22 }}>Welcome!</h1>
            <p className="auth-sub" style={{ margin: "6px 0 0", fontSize: 13.5 }}>
              Sign in to discover conversations that matter.
            </p>
          </div>
  
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => startOAuth("oauth_google", "google")}
              disabled={disabled}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                height: 44,
                padding: "0 16px",
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "var(--bg-card)",
                color: "var(--text-primary)",
                fontSize: 14,
                fontWeight: 500,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled && pending !== "google" ? 0.55 : 1,
                transition: "background 120ms var(--ease), border-color 120ms var(--ease)",
              }}
            >
              {pending === "google" ? (
                <span
                  aria-hidden="true"
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "2px solid color-mix(in srgb, var(--text-primary) 22%, transparent)",
                    borderTopColor: "var(--text-primary)",
                    animation: "authButtonSpin 700ms linear infinite",
                    flex: "0 0 16px",
                  }}
                />
              ) : (
                GoogleMark
              )}
              <span>Sign in with Google</span>
            </button>
  
            <button
              type="button"
              onClick={() => startOAuth("oauth_apple", "apple")}
              disabled={disabled}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                height: 44,
                padding: "0 16px",
                borderRadius: 10,
                border: "1px solid #000",
                background: "#000",
                color: "#fff",
                fontSize: 14,
                fontWeight: 500,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled && pending !== "apple" ? 0.55 : 1,
                transition: "opacity 120ms var(--ease)",
              }}
            >
              {pending === "apple" ? (
                <span
                  aria-hidden="true"
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "2px solid rgba(255, 255, 255, 0.28)",
                    borderTopColor: "#fff",
                    animation: "authButtonSpin 700ms linear infinite",
                    flex: "0 0 16px",
                  }}
                />
              ) : (
                AppleMark
              )}
              <span>Sign in with Apple</span>
            </button>
          </div>
  
          {status === "loading" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--text-tertiary)", fontSize: 12.5 }}>
              <window.I.Refresh size={13} style={{ animation: "spin 900ms linear infinite" }} />
              Preparing secure sign-in…
            </div>
          )}
  
          {status === "error" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--danger)", fontSize: 12.5 }}>
              <window.I.ShieldCheck size={13} />
              {errorMsg || "Sign-in unavailable."}
            </div>
          )}
  
          <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--text-quaternary)", lineHeight: 1.5 }}>
            By continuing, you agree to our{" "}
            <Link to="/terms" style={{ textDecoration: "underline" }}>
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" style={{ color: "inherit", textDecoration: "underline" }}>
              Privacy Policy
            </Link>
            .
          </p>
        </div>
  
        <div className="auth-trust">
          <window.I.ShieldCheck size={13} />
          Protected by Clerk.
        </div>
      </div>
    );
  }
  
  window.LoginScreen = LoginScreen;
  
